import { Box, Typography } from "@mui/material";
import { ContextOrganizationFieldMode } from "components/ContextOrganizationField";
import RedirectBackButton from "components/RedirectBackButton";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  MediaItemSaveRequest,
  MediaItemSource,
  SaveResponse,
} from "services/KnowfullyAdminClient";
import { MediaFilterType } from "utils/mediaUtils";
import translateError from "utils/translateError";
import MediaLibraryItemForm, {
  MediaLibraryItemFormValues,
} from "./MediaLibraryItemForm";

type MediaLibraryItemCreateFormProps = {
  source?: MediaItemSource;
  options?: MediaFilterType[];
  customBackButtonLabel?: string;
  alternativeTextRequired?: boolean;
  customAlternativeTextLabel?: string;
  organizationPickerMode?: ContextOrganizationFieldMode;
  organizationIdOverride?: string;
  maxFileSizeBytes?: number;
  onRedirectBack?: () => void;
};

const MediaLibraryItemCreateForm = ({
  source = MediaItemSource.MediaLibrary,
  options,
  customBackButtonLabel,
  alternativeTextRequired,
  customAlternativeTextLabel,
  organizationPickerMode,
  organizationIdOverride,
  maxFileSizeBytes,
  onRedirectBack,
}: MediaLibraryItemCreateFormProps) => {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const { mediaService, mediaLibraryUrl } = useGlobalContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useTitle(t({ id: "mediaLibraryItemFormCreateTitle" }));

  const onCancel = () => {
    redirect();
  };

  const redirect = () => {
    onRedirectBack ? onRedirectBack() : navigate(mediaLibraryUrl());
  };

  const onSubmit = async (
    values: MediaLibraryItemFormValues,
    _: File | null
  ) => {
    try {
      const newMedia: SaveResponse = await registerPromise(
        mediaService.saveItem(
          source,
          MediaItemSaveRequest.fromJS({
            ...values,
          })
        )
      );

      queryClient.invalidateQueries("media-items");
      redirect();

      return newMedia;
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  return (
    <>
      <Box my={2}>
        <RedirectBackButton
          redirectRoute={mediaLibraryUrl()}
          onRedirectBack={onRedirectBack}
          labelKey={customBackButtonLabel ?? "backToMediaLibraryLabel"}
        />
      </Box>
      <Typography variant="h5">
        <T id="mediaLibraryItemFormCreateTitle" />
      </Typography>
      <MediaLibraryItemForm
        source={source}
        organizationPickerMode={organizationPickerMode}
        organizationIdOverride={organizationIdOverride}
        options={options}
        onSubmit={onSubmit}
        submitLabel={t({ id: "uploadFileLabel" })}
        onCancel={onCancel}
        alternativeTextRequired={alternativeTextRequired}
        customAlternativeTextLabel={customAlternativeTextLabel}
        maxFileSizeBytes={maxFileSizeBytes}
      />
    </>
  );
};

export default MediaLibraryItemCreateForm;
